import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Sparkles,
  BookOpen,
  Play,
  CheckCircle2,
  ArrowRight,
  Rocket,
  KeyRound,
  GraduationCap,
  Smartphone,
  FlaskConical,
  ChevronDown,
  Quote,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import Autoplay from 'embla-carousel-autoplay'
import Footer from '@/components/Footer'
import ScrollReveal from '@/components/ScrollReveal'
import MasterclassCard from '@/components/MasterclassCard'
import { apiClient } from '@/services/api'
import { ENDPOINTS } from '@/config/api.config'
import { Skeleton } from '@/components/ui/skeleton'
import { useCourses } from '@/hooks/useCourses'

const FloatingCircles = () => (
  <>
    <motion.div
      className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none"
      animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute bottom-10 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none"
      animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
    />
    <motion.div
      className="absolute top-[15%] right-[15%] w-5 h-5 bg-primary/40 rounded-full pointer-events-none"
      animate={{ y: [0, -15, 0], opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute top-[35%] left-[8%] w-3 h-3 bg-primary/30 rounded-full pointer-events-none"
      animate={{ y: [0, 20, 0], opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
    />
    <motion.div
      className="absolute bottom-[30%] right-[8%] w-6 h-6 bg-primary/35 rounded-full pointer-events-none"
      animate={{ y: [0, -18, 0], x: [0, 8, 0], opacity: [0.25, 0.55, 0.25] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
    />
  </>
)

const AccessTicket = ({
  code,
  setCode,
  codeError,
  setCodeError,
  handleAccess,
  validating,
  width,
}: {
  code: string
  setCode: (v: string) => void
  codeError: string
  setCodeError: (v: string) => void
  handleAccess: () => void
  validating?: boolean
  width?: number
}) => (
  <div className="relative w-full" style={{ width: width ?? 384 }}>
    {/* Glow animado */}
    <motion.div
      className="absolute -inset-1 rounded-lg opacity-60"
      style={{
        background: 'linear-gradient(90deg, hsl(18 89% 54%), hsl(40 100% 72%), hsl(18 89% 54%))',
        backgroundSize: '200% auto',
        filter: 'blur(10px)',
      }}
      animate={{ backgroundPosition: ['0% center', '200% center'] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
    />

    {/* Ingresso */}
    <div className="relative" style={{ minHeight: '200px' }}>
      {/* SVG: scallops arredondados nos dois lados — ticket simétrico */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 400 200"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {/*
          Scallops concavos (meio-círculo para dentro) em ambos os lados curtos.
          5 scallops por lado, raio 20, cada um ocupa 40px de altura.
          Caminho clockwise: topo → lado direito ↓ → base → lado esquerdo ↑
        */}
        {/*
          4 scallops por lado, raio 16, chord 32px.
          Entre cada arco: 8px de linha reta → sem pontinhas, conexão reta.
          Cantos: Q bezier raio 16 nos 4 cantos.
          Layout vertical (y): 16(canto) + [8+32]×4 + 8 + 16(canto) = 200 ✓
        */}
        <path
          d="M 16,0 L 384,0 Q 400,0 400,16
             L 400,24 A 16,16 0 0,0 400,56
             L 400,64 A 16,16 0 0,0 400,96
             L 400,104 A 16,16 0 0,0 400,136
             L 400,144 A 16,16 0 0,0 400,176
             L 400,184 Q 400,200 384,200
             L 16,200 Q 0,200 0,184
             L 0,176 A 16,16 0 0,0 0,144
             L 0,136 A 16,16 0 0,0 0,104
             L 0,96  A 16,16 0 0,0 0,64
             L 0,56  A 16,16 0 0,0 0,24
             L 0,16 Q 0,0 16,0 Z"
          style={{
            fill: 'hsl(var(--background))',
            stroke: 'hsl(18 89% 54% / 0.4)',
            strokeWidth: '1',
          }}
        />
        {/* Borda interna */}
        <rect
          x="24"
          y="16"
          width="352"
          height="168"
          rx="8"
          style={{ fill: 'none', stroke: 'hsl(18 89% 54% / 0.15)', strokeWidth: '0.8' }}
        />
      </svg>

      {/* Conteúdo HTML */}
      <div className="relative z-10 flex h-full" style={{ minHeight: '200px' }}>
        {/* Stub esquerdo */}
        <div
          className="hidden sm:flex items-center justify-center shrink-0"
          style={{ width: '88px' }}
        >
          <span
            className="text-[9px] text-primary/35 font-mono tracking-[0.2em] uppercase whitespace-nowrap select-none"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            Acesso
          </span>
        </div>

        {/* Área principal */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-4 py-5 space-y-2.5">
          <div className="flex items-center justify-center gap-1.5">
            <KeyRound
              className="w-3.5 h-3.5 text-primary shrink-0"
              style={{ filter: 'drop-shadow(0 0 4px hsl(18 89% 54%))' }}
            />
            <span
              className="text-sm font-bold tracking-widest uppercase text-primary"
              style={{ textShadow: '0 0 8px hsl(18 89% 54% / 0.7)' }}
            >
              Acesso Exclusivo
            </span>
          </div>
          <p className="text-base text-muted-foreground text-center leading-relaxed">
            Insira seu código para se cadastrar
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Código de acesso"
              value={code}
              onChange={(e) => {
                setCode(e.target.value)
                setCodeError('')
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleAccess()}
              className="h-9 bg-muted/50 border-primary/20 focus:border-primary text-sm"
            />
            <motion.button
              whileHover={{ scale: validating ? 1 : 1.05 }}
              whileTap={{ scale: validating ? 1 : 0.95 }}
              onClick={handleAccess}
              disabled={validating}
              className="btn-primary whitespace-nowrap flex items-center justify-center gap-1.5 text-sm px-3 h-9 disabled:opacity-70 w-full sm:w-auto"
            >
              {validating ? (
                'Validando...'
              ) : (
                <>
                  Acessar <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </motion.button>
          </div>
          {codeError && <p className="text-xs text-destructive text-center">{codeError}</p>}
        </div>

        {/* Stub direito (espelho) */}
        <div
          className="hidden sm:flex items-center justify-center shrink-0"
          style={{ width: '88px' }}
        >
          <span
            className="text-[9px] text-primary/35 font-mono tracking-[0.2em] uppercase whitespace-nowrap select-none"
            style={{ writingMode: 'vertical-rl' }}
          >
            AI · SYM
          </span>
        </div>
      </div>
    </div>
  </div>
)

const stats = [
  {
    title: 'Sem pré-requisitos',
    description: 'Aprenda a usar IA do zero desde o primeiro dia, sem complicação.',
    icon: GraduationCap,
  },
  {
    title: 'Microaprendizado prático',
    description: 'Substitua o scroll infinito nas redes sociais por aprendizados úteis.',
    icon: Smartphone,
  },
  {
    title: '20+ Conteúdos',
    description: 'Acesse conteúdos novos toda semana: Drops, Episódios e Masterclasses.',
    icon: Play,
  },
  {
    title: 'Métodos Exclusivos',
    description:
      'Descubra o Framework FCamara para prompts profissionais e o Método TER para avaliar seus resultados com a IA.',
    icon: FlaskConical,
  },
]

const testimonials = [
  {
    name: 'Pedro Balduino',
    text: 'Excelente maneira de passar o conteúdo, muito explicativo e dinâmico. Foi super bacana fazer os exercícios propostos e começar a utilizar essas ferramentas de engenharia de contexto no dia a dia.',
  },
  {
    name: 'Marcela An',
    text: 'As apresentações foram muito didáticas. Explicações claras, dicas fundamentais para um bom desenvolvimento e aplicações dentro das LLMs.',
  },
  {
    name: 'Daniele Rego',
    text: 'Curso extremamente relevante e aplicável à rotina profissional. O conteúdo é bem estruturado, prático e fácil de colocar em uso no dia a dia, ajudando a evoluir o uso da IA de interações pontuais para uma abordagem mais estratégica e consistente. Traz frameworks claros, exemplos reais e orientações que aumentam a qualidade, a previsibilidade e a eficiência dos resultados.',
  },
  {
    name: 'Rodrigo Araújo',
    text: 'Curso direto ao ponto, objetivo, com foco na solução do problema.',
  },
  {
    name: 'Davi Oliveira',
    text: 'Muito bom o conteúdo apresentado. Fiquei surpreso com o tanto de coisa que já aplico no dia a dia e foi ministrado nesse curso, sinal que estou no caminho certo! Parabéns aos envolvidos.',
  },
  {
    name: 'Kleber Santos',
    text: 'Curti tudo, desde o tamanho das aulas e mensagens diretas sem dar voltas, até a qualidade do conteúdo.',
  },
  {
    name: 'Antônio Gambale',
    text: 'Conteúdo e didática incríveis. Tudo foi explicado de forma simples e clara, já me inspirou a colocar a mão na massa!',
  },
  {
    name: 'Fabio Sousa',
    text: 'Muito bom, aprendi técnicas que não conhecia e venho aplicando no meu trabalho.',
  },
  {
    name: 'Raul Rocha',
    text: 'Excelente. Muito didático e com conteúdo top. Os testes no final ajudam a fixar todo o conteúdo.',
  },
  {
    name: 'Elexsandro Silva',
    text: 'O curso foi bem explicado. Gostei dos exemplos e das dicas sobre como usar a IA de forma inteligente. Muito útil para o dia a dia!',
  },
  {
    name: 'Ricardo Henrique',
    text: 'Rápido e direto. Perfeito para os dias atuais. Ajuda a entender os principais conceitos de engenharia de prompt e de contexto.',
  },
]

const modules = [
  {
    number: '01',
    title: 'Mentalidade orientada à IA',
    description:
      'Descubra como a IA realmente funciona, o que ela pode (e não pode) fazer, e como desenvolver suas habilidades para aproveitar o máximo dela.',
    episodes: [
      'Ep 1.1 - Como Ter Uma Mentalidade Orientada à IA',
      'Ep 1.2 - Termos Básicos de IA Que Todo Mundo Precisa Saber',
      'Ep 1.3 - Capacidades, Limites e Ética da IA',
      'Ep 1.4 - Boas Práticas de Privacidade na IA',
      'Ep 1.5 - Como Aprimorar Sua Inteligência Humana Usando IA',
      'Ep 1.6 - Como Avaliar se a IA Está Ajudando',
    ],
  },
  {
    number: '02',
    title: 'Engenharia de Prompt',
    description:
      'Aprenda a se comunicar com a IA de forma clara e eficiente. Você vai conhecer e praticar técnicas e o Framework FCAMARA, um método exclusivo que transforma pedidos vagos em resultados profissionais.',
    episodes: [
      'Ep 2.1 - Básico de Bons Prompts',
      'Ep 2.2 - Framework FCAMARA para Prompt Profissionais',
      'Ep 2.3 - Técnicas de Precisão, Multimodal e Formatos Específicos',
      'Ep 2.4 - Técnicas Avançadas - Few-Shot, Delimitadores e Flip The Script',
      'Ep 2.5 - Chain-of-Thought – Fazendo a IA Pensar Alto',
      'Ep 2.6 - Biblioteca de Prompts, Iteração e Meta-Prompting',
    ],
  },
  {
    number: '03',
    title: 'Engenharia de Contexto',
    description:
      'Domine a arte de criar contexto: como fornecer as informações certas, no formato certo, para que a IA entenda exatamente o que você precisa para te responder com precisão.',
    episodes: [
      'Ep 3.1 - Fundamentos de Engenharia de Contexto',
      'Ep 3.2 - Anatomia de Contexto e Tokenização',
      'Ep 3.3 - RAG: Como Conectar Seus Dados à IA',
      'Ep 3.4 - 4 Falhas de Contexto e Como Evitá-las',
      'Ep 3.5 - Memória: Como a IA Lembra e Esquece',
      'Ep 3.6 - Pense Como Um Engenheiro de Contexto',
    ],
  },
]

const Landing = () => {
  const heroRef = useRef<HTMLElement>(null)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [validating, setValidating] = useState(false)
  const [expandedModule, setExpandedModule] = useState<string | null>(null)
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }))
  const autoplayMasterclass = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }))
  const titleSpanRef = useRef<HTMLSpanElement>(null)
  const paragraphRef = useRef<HTMLParagraphElement>(null)
  const [ticketWidth, setTicketWidth] = useState<number>(384)

  // Masterclasses da seção — publicadas + em breve
  const { data: masterclasses, isLoading: isLoadingMasterclasses } = useCourses({
    type: 'masterclass',
    status: 'published,coming_soon',
  })

  useEffect(() => {
    const measure = () => {
      const p = paragraphRef.current?.getBoundingClientRect().width
      const t = titleSpanRef.current?.getBoundingClientRect().width
      const w = p && p > 0 ? p : t && t > 0 ? t : 0
      if (w > 0) setTicketWidth(w)
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const handleAccess = async () => {
    const trimmed = code.trim().toUpperCase()
    if (!trimmed) {
      setCodeError('Insira um código de acesso.')
      return
    }

    setValidating(true)
    setCodeError('')

    try {
      const result = await apiClient.get<{ valid: boolean }>(
        `${ENDPOINTS.coupons.validate}?code=${encodeURIComponent(trimmed)}`
      )

      if (result.valid) {
        window.location.href = `/signup?cupom=${encodeURIComponent(trimmed)}`
      } else {
        setCodeError('Código inválido. Verifique e tente novamente.')
      }
    } catch {
      setCodeError('Código inválido. Verifique e tente novamente.')
    } finally {
      setValidating(false)
    }
  }
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  })
  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '80%'])
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.9, 0.4])

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar simples */}
      <header
        className="fixed left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-md"
        style={{ top: 'var(--maintenance-banner-height, 0px)' }}
      >
        <div className="container relative flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-1">
            <span className="text-xl font-bold text-gradient">AI</span>
            <span className="text-xl font-bold text-foreground">Sym</span>
          </Link>
          {/* Menu "Para Empresas" ocultado temporariamente — volta em breve.
              Rota (/para-empresas em App.tsx) e página (ParaEmpresas.tsx) mantidas intactas. */}
          {/* <Link
            to="/para-empresas"
            className="absolute left-1/2 -translate-x-1/2 text-sm font-medium text-foreground hover:text-primary transition-colors"
          >
            Para Empresas
          </Link> */}
          <div className="flex items-center gap-3">
            <Link to="/auth" className="btn-primary text-sm py-2 px-4">
              Já tenho uma conta
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-[calc(100svh-5rem)] flex flex-col justify-center pt-20 pb-8 overflow-hidden"
      >
        <motion.div className="gradient-hero absolute inset-0" style={{ y: backgroundY }} />
        <FloatingCircles />

        <motion.div className="container relative" style={{ y: textY, opacity }}>
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <motion.div
              className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm text-primary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <Sparkles className="w-4 h-4" />
              </motion.div>
              <span>Powered by FCamara</span>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Domine a<br />
              <span ref={titleSpanRef} className="text-shine">
                Inteligência Artificial
              </span>
            </motion.h1>

            <motion.p
              ref={paragraphRef}
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Você tem 5 minutos? Então aproveite para aprender algo útil e prático para o seu dia a
              dia usando inteligência artificial.
            </motion.p>

            {/* Faixa de interdição */}
            <div
              className="w-screen relative left-1/2 -translate-x-1/2 overflow-hidden py-3 my-2"
              style={{
                background: 'hsl(18 89% 54% / 0.08)',
                boxShadow:
                  '0 0 24px 6px hsl(18 89% 54% / 0.35), inset 0 0 40px 8px hsl(18 89% 54% / 0.15)',
                borderTop: '1px solid hsl(18 89% 54% / 0.4)',
                borderBottom: '1px solid hsl(18 89% 54% / 0.4)',
              }}
            >
              <motion.div
                className="flex whitespace-nowrap"
                animate={{ x: ['0%', '-50%'] }}
                transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
              >
                {(() => {
                  const ticker = Array(30).fill('• Acesso Exclusivo ').join('')
                  return (
                    <span
                      className="text-primary font-bold text-sm tracking-widest uppercase"
                      style={{ textShadow: '0 0 10px hsl(18 89% 54% / 0.8)' }}
                    >
                      {ticker}
                      {ticker}
                    </span>
                  )
                })()}
              </motion.div>
            </div>

            <motion.div
              className="flex flex-col items-center gap-3 pt-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <AccessTicket
                code={code}
                setCode={setCode}
                codeError={codeError}
                setCodeError={setCodeError}
                handleAccess={handleAccess}
                validating={validating}
                width={ticketWidth}
              />
              <Link
                to="/auth"
                className="text-sm text-muted-foreground hover:text-primary transition-colors mt-1"
              >
                Já tem uma conta? <span className="text-primary">Acesse aqui</span>
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-secondary/30 border-y border-border">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.map(({ title, description, icon: Icon }, i) => (
              <ScrollReveal key={title} delay={i * 0.1}>
                <div className="bg-card border border-border rounded-xl p-5 space-y-4 hover:border-primary/40 transition-colors h-full">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-1.5">
                    <p className="font-bold text-foreground leading-snug">{title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-background relative overflow-hidden">
        {/* LEDs animados */}
        <motion.div
          className="absolute top-10 left-[10%] w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_3px_hsl(18_89%_54%/0.6)]"
          animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.4, 1] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-[30%] left-[4%] w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_6px_2px_hsl(18_89%_54%/0.5)]"
          animate={{ opacity: [0.3, 0.9, 0.3], scale: [1, 1.3, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        />
        <motion.div
          className="absolute bottom-[20%] left-[8%] w-2.5 h-2.5 rounded-full bg-primary shadow-[0_0_10px_4px_hsl(18_89%_54%/0.5)]"
          animate={{ opacity: [0.5, 1, 0.5], scale: [1, 1.5, 1] }}
          transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        <motion.div
          className="absolute top-[15%] right-[6%] w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_3px_hsl(18_89%_54%/0.6)]"
          animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.4, 1] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
        />
        <motion.div
          className="absolute top-[60%] right-[4%] w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_6px_2px_hsl(18_89%_54%/0.5)]"
          animate={{ opacity: [0.3, 0.8, 0.3], scale: [1, 1.3, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
        />
        <motion.div
          className="absolute bottom-[10%] right-[10%] w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_3px_hsl(18_89%_54%/0.6)]"
          animate={{ opacity: [0.4, 1, 0.4], scale: [1, 1.4, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }}
        />
        <motion.div
          className="absolute top-[45%] left-[2%] w-1 h-1 rounded-full bg-primary shadow-[0_0_5px_2px_hsl(18_89%_54%/0.4)]"
          animate={{ opacity: [0.2, 0.7, 0.2], scale: [1, 1.5, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
        <motion.div
          className="absolute bottom-[40%] right-[2%] w-1 h-1 rounded-full bg-primary shadow-[0_0_5px_2px_hsl(18_89%_54%/0.4)]"
          animate={{ opacity: [0.2, 0.8, 0.2], scale: [1, 1.6, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
        />

        <div className="container relative">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="module-badge mb-4">Depoimentos</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4">
                Quem fez, aprovou.
              </h2>
              <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
                Veja o que dizem as pessoas que já concluíram a Jornada IA.
              </p>
            </div>
          </ScrollReveal>

          <ScrollReveal>
            <div
              onMouseEnter={() => autoplay.current.stop()}
              onMouseLeave={() => autoplay.current.play()}
            >
              <Carousel
                opts={{ align: 'start', loop: true, duration: 60 }}
                plugins={[autoplay.current]}
                className="w-full"
              >
                <CarouselContent className="-ml-4">
                  {testimonials.map(({ name, text }) => (
                    <CarouselItem key={name} className="pl-4 sm:basis-1/2 lg:basis-1/3">
                      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 h-full space-y-4 hover:border-primary/50 hover:bg-primary/10 transition-colors flex flex-col">
                        <Quote className="w-6 h-6 text-primary/60 shrink-0" />
                        <p className="text-sm text-foreground/80 leading-relaxed flex-1">{text}</p>
                        <p className="text-sm font-semibold text-primary">{name}</p>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="hidden sm:flex" />
                <CarouselNext className="hidden sm:flex" />
              </Carousel>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Modules */}
      <section className="py-20 bg-secondary/30">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-14">
              <span className="module-badge mb-4">Curso Jornada IA</span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-4">
                Comece pelo básico bem feito.
              </h2>
              <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
                Do conceito à prática, aprendizado consistente e progressivo na sua evolução com a
                IA.
              </p>
            </div>
          </ScrollReveal>

          <div className="max-w-3xl mx-auto space-y-4">
            {modules.map(({ number, title, description, episodes }, i) => {
              const isExpanded = expandedModule === number
              const hasEpisodes = episodes.length > 0
              return (
                <ScrollReveal key={number} delay={i * 0.08}>
                  <div
                    className={`bg-card border rounded-xl transition-colors ${isExpanded ? 'border-primary/60' : 'border-border hover:border-primary/40'}`}
                  >
                    <div
                      className={`flex items-start gap-5 p-5 ${hasEpisodes ? 'cursor-pointer' : ''}`}
                      onClick={() => hasEpisodes && setExpandedModule(isExpanded ? null : number)}
                    >
                      <span
                        className={`text-3xl font-bold font-mono leading-none mt-1 transition-colors ${isExpanded ? 'text-primary/60' : 'text-primary/30'}`}
                      >
                        {number}
                      </span>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{description}</p>
                      </div>
                      {hasEpisodes ? (
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="shrink-0 mt-1"
                        >
                          <ChevronDown className="w-5 h-5 text-primary/50" />
                        </motion.div>
                      ) : (
                        <CheckCircle2 className="w-5 h-5 text-primary/30 shrink-0 mt-0.5" />
                      )}
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <ul className="px-5 pb-5 space-y-2 border-t border-border pt-4">
                            {episodes.map((ep) => (
                              <li
                                key={ep}
                                className="flex items-center gap-2 text-sm text-muted-foreground"
                              >
                                <Play className="w-3 h-3 text-primary/50 shrink-0" />
                                {ep}
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </ScrollReveal>
              )
            })}

            <ScrollReveal delay={0.3}>
              <div className="flex items-center gap-5 bg-card border border-dashed border-border rounded-xl p-5 opacity-60">
                <span className="text-3xl font-bold text-primary/20 font-mono leading-none mt-1">
                  ···
                </span>
                <div className="flex-1">
                  <h3 className="font-semibold text-muted-foreground">Mais conteúdos em breve</h3>
                  <p className="text-sm text-muted-foreground/70 mt-1">
                    A Jornada IA continua. Novos módulos serão lançados em breve.
                  </p>
                </div>
                <Sparkles className="w-5 h-5 text-primary/20 shrink-0" />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Masterclasses */}
      <section className="py-16 bg-background relative z-10">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="module-badge mb-4">Premium</span>
              <h2
                className="text-4xl md:text-5xl font-semibold mt-4 text-gradient"
                style={{ fontFamily: 'Caveat, cursive' }}
              >
                Masterclasses
              </h2>
              <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
                Aulas aprofundadas com especialistas do mercado para levar seus conhecimentos ao
                próximo nível.
              </p>
            </div>
          </ScrollReveal>
          <div className="px-12">
            {isLoadingMasterclasses ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-lg border border-border bg-card overflow-hidden">
                    <Skeleton className="aspect-video w-full rounded-none" />
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-5 w-1/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  </div>
                ))}
              </div>
            ) : masterclasses && masterclasses.length > 0 ? (
              <div
                onMouseEnter={() => autoplayMasterclass.current.stop()}
                onMouseLeave={() => autoplayMasterclass.current.play()}
              >
                <Carousel
                  opts={{ align: 'start', loop: true }}
                  plugins={[autoplayMasterclass.current]}
                  className="w-full"
                >
                  <CarouselContent className="-ml-4">
                    {masterclasses.map((course) => (
                      <CarouselItem key={course.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                        <MasterclassCard course={course} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="-left-10" />
                  <CarouselNext className="-right-10" />
                </Carousel>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                Novas masterclasses em breve.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-32 bg-secondary/30 border-y border-border relative overflow-hidden">
        <motion.div className="gradient-hero absolute inset-0 opacity-60" />
        <FloatingCircles />

        <div className="container relative">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h2 className="text-4xl md:text-6xl font-bold text-foreground leading-tight">
                O futuro já é o presente.{' '}
                <span className="text-shine">Comece a aprender mais sobre IA agora!</span>
              </h2>

              <p className="text-muted-foreground text-xl max-w-2xl mx-auto">
                Quem entende IA trabalha mais rápido, entrega mais e se destaca. Comece agora com
                quem já está nessa jornada.
              </p>

              <div className="flex flex-col items-center gap-3 pt-2">
                <AccessTicket
                  code={code}
                  setCode={setCode}
                  codeError={codeError}
                  setCodeError={setCodeError}
                  handleAccess={handleAccess}
                  validating={validating}
                  width={ticketWidth}
                />
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Landing
